import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useSearchParams } from 'react-router';
import { ActivityDialog } from './activity-dialog';
import { ActivityStatusSelect } from './status-select';
import { ContentBox } from '@/components/shared/content-box';
import { MarkdownRenderer } from '@/components/shared/markdown-renderer';
import { ActivityType } from '@/data/idp/interfaces';
import { type Activity, PlanStatus } from '@/data/idp/plans/interfaces';
import { SelectedCourseCard } from '@/packages/idp/components/selected-course-card';
import { SelectedResourceCard } from '@/packages/idp/pages/show-plan/components/activity-list/components/activity-card/selected-resource-card';
import { lineClamp } from '@/utils/line-clamp';
import { DeleteActivityDialog } from './delete-activity-dialog';

interface ActivityCardProps {
  activity: Activity;
  planStatus: PlanStatus;
}

export const ActivityCard = ({ activity, planStatus }: ActivityCardProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activityId = searchParams.get('atividade');
  const { attached_resource, recommended_activity, description, title, id } =
    activity;
  const [showActivityDialog, setShowActivityDialog] = useState(
    activityId === id
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const activityType = recommended_activity?.activity_type;
  const externalLink = attached_resource?.external_link;
  const course = attached_resource?.course;
  const hasAttachedResource = course || externalLink;
  const shouldShowCourseButton = !hasAttachedResource;
  const planInProgress = planStatus === PlanStatus.InProgress;
  const planInDraft = planStatus === PlanStatus.Draft;

  const handleCloseActivityDialog = () => {
    setShowActivityDialog(false);
    searchParams.delete('atividade');
    setSearchParams(searchParams, { replace: true });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <ContentBox height="100%" justifyContent="space-between" p={2} bordered>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Box>
              {planInProgress && (
                <ActivityStatusSelect
                  activity={activity}
                  planStatus={planStatus}
                />
              )}
            </Box>
            {(planInProgress || planInDraft) && (
              <>
                <IconButton onClick={handleMenuOpen}>
                  <MoreVertIcon
                    fontSize="small"
                    sx={{ color: 'primary.dark' }}
                  />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      setShowActivityDialog(true);
                    }}
                  >
                    Editar
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      setShowDeleteDialog(true);
                    }}
                  >
                    Deletar
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
          <Typography variant="h2" mt={1}>
            {title}
          </Typography>
          <Box component="span" sx={lineClamp(4)}>
            <MarkdownRenderer markdown={description} />
          </Box>
        </Stack>
        <Stack alignItems="flex-start" gap={2} width="100%" mt={2}>
          {course && <SelectedCourseCard course={course} />}
          {externalLink && <SelectedResourceCard resource={externalLink} />}
          {shouldShowCourseButton && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setShowActivityDialog(true)}
              className="media-print-hide"
            >
              {activityType === ActivityType.Course
                ? 'Ver cursos recomendados'
                : 'Buscar na plataforma'}
            </Button>
          )}
        </Stack>
      </ContentBox>
      {showActivityDialog && (
        <ActivityDialog
          planStatus={planStatus}
          activity={activity}
          onClose={handleCloseActivityDialog}
        />
      )}
      {showDeleteDialog && (
        <DeleteActivityDialog
          onClose={() => setShowDeleteDialog(false)}
          activity={activity}
          planId={activity.plan_id}
        />
      )}
    </>
  );
};
